import React from 'react';

export type TableModel = {
  rows: number;
  cols: number;
  data: string[][]; // [row][col]
  colWidths?: number[]; // px, length == cols
  rowHeights?: number[]; // px, length == rows
  dense?: boolean;
  bordered?: boolean;
  fitCols?: boolean; // squeeze columns to always fit container width
  fitRows?: boolean; // use minimal row height to show more rows
  scrollable?: boolean; // allow scrollbars when not in fit mode
  hoverOutline?: boolean; // show dotted outline on hover in editor wrappers
};

type Props = TableModel & {
  editable?: boolean;
  showResizeHandles?: boolean;
  onChange?: (next: TableModel) => void;
};

const clamp = (v: number, min: number, max: number) => Math.max(min, Math.min(max, v));

const BASE_MIN_COL_W = 48;
const BASE_MIN_ROW_H = 28;

const Table: React.FC<Props> = ({ rows, cols, data, colWidths = [], rowHeights = [], dense = false, bordered = true, fitCols = false, fitRows = false, scrollable = false, hoverOutline = true, editable = false, showResizeHandles = false, onChange }) => {
  const rootRef = React.useRef<HTMLDivElement | null>(null);
  const [drag, setDrag] = React.useState<null | { kind: 'col' | 'row'; index: number; start: number; sizes: number[] }>(null);

  const sizesForCols = React.useMemo(() => {
    if (!cols) return [] as number[];
    const base = colWidths.length === cols ? colWidths.slice() : new Array(cols).fill(0);
    const minCol = fitCols ? 32 : BASE_MIN_COL_W;
    // Fill 0s with equal widths based on container width
    const total = base.reduce((a, b) => a + b, 0);
    const host = rootRef.current; const w = host ? host.clientWidth : 600;
    if (fitCols) {
      const each = Math.max(minCol, Math.floor((w - (cols - 1)) / cols));
      return new Array(cols).fill(each);
    }
    if (total <= 0) {
      const each = Math.max(minCol, Math.floor((w - (cols - 1)) / cols));
      return new Array(cols).fill(each);
    }
    return base.map(v => Math.max(minCol, v || (total/cols)));
  }, [cols, colWidths, fitCols]);

  const sizesForRows = React.useMemo(() => {
    if (!rows) return [] as number[];
    const base = rowHeights.length === rows ? rowHeights.slice() : new Array(rows).fill(0);
    const total = base.reduce((a, b) => a + b, 0);
    const minRow = fitRows ? 22 : BASE_MIN_ROW_H;
    if (total <= 0) return new Array(rows).fill(fitRows ? minRow : (dense ? 36 : 44));
    return base.map(v => Math.max(minRow, v || (total/rows)));
  }, [rows, rowHeights, dense, fitRows]);

  const startDragCol = (e: React.MouseEvent, i: number) => {
    e.preventDefault(); e.stopPropagation();
    setDrag({ kind: 'col', index: i, start: e.clientX, sizes: sizesForCols.slice() });
  };
  const startDragRow = (e: React.MouseEvent, i: number) => {
    e.preventDefault(); e.stopPropagation();
    setDrag({ kind: 'row', index: i, start: e.clientY, sizes: sizesForRows.slice() });
  };

  React.useEffect(() => {
    if (!drag) return;
    const onMove = (ev: MouseEvent) => {
      if (!onChange) return;
      if (drag.kind === 'col') {
        const dx = ev.clientX - drag.start;
        const sizes = drag.sizes.slice();
        const i = drag.index;
        const minCol = fitCols ? 32 : BASE_MIN_COL_W;
        sizes[i] = clamp((sizes[i] || 0) + dx, minCol, 2000);
        const next: TableModel = { rows, cols, data, colWidths: sizes, rowHeights: sizesForRows, dense, bordered, fitCols, fitRows, scrollable, hoverOutline };
        onChange(next);
      } else {
        const dy = ev.clientY - drag.start;
        const sizes = drag.sizes.slice();
        const i = drag.index;
        const minRow = fitRows ? 22 : BASE_MIN_ROW_H;
        sizes[i] = clamp((sizes[i] || 0) + dy, minRow, 2000);
        const next: TableModel = { rows, cols, data, colWidths: sizesForCols, rowHeights: sizes, dense, bordered, fitCols, fitRows, scrollable, hoverOutline };
        onChange(next);
      }
    };
    const onUp = () => setDrag(null);
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp, { once: true });
    return () => { window.removeEventListener('mousemove', onMove); window.removeEventListener('mouseup', onUp); };
  }, [drag, onChange, rows, cols, data, dense, bordered, sizesForCols, sizesForRows]);

  const updateCell = (r: number, c: number, val: string) => {
    if (!onChange) return;
    const nextData = data.map((row, ri) => row.map((cell, ci) => (ri === r && ci === c ? val : cell)));
    onChange({ rows, cols, data: nextData, colWidths: sizesForCols, rowHeights: sizesForRows, dense, bordered, fitCols, fitRows, scrollable, hoverOutline });
  };

  const gridCols = sizesForCols.map(w => `${w}px`).join(' ');

  return (
    <div ref={rootRef} className={`relative min-w-0 w-full max-w-full ${bordered ? 'rounded-xl border border-gray-200 shadow-sm' : ''} bg-white ${(fitCols || fitRows) ? 'overflow-hidden' : (scrollable ? 'overflow-auto' : 'overflow-hidden')}`}
      style={{ padding: (dense || fitRows || fitCols) ? 6 : 10 }}
      onMouseDown={() => { /* allow parent to select */ }}
    >
      <div className="relative" style={{ display: 'grid', gridTemplateColumns: gridCols }}>
        {Array.from({ length: rows }).map((_, r) => (
          <React.Fragment key={r}>
            {Array.from({ length: cols }).map((__, c) => (
              <div key={`${r}-${c}`} className={`relative ${bordered ? 'border border-gray-200' : ''}`} style={{ height: sizesForRows[r] || (fitRows ? 22 : (dense ? 36 : 44)) }}>
                {editable ? (
                  <div
                    className={`w-full h-full ${fitCols || fitRows ? 'px-1 py-0.5 text-xs' : 'px-2 py-1 text-sm'} outline-none`}
                    contentEditable
                    suppressContentEditableWarning
                    onInput={(e) => updateCell(r, c, (e.currentTarget.textContent || ''))}
                    onBlur={(e) => updateCell(r, c, (e.currentTarget.textContent || ''))}
                  >
                    {data[r]?.[c] || ''}
                  </div>
                ) : (
                  <div className={`w-full h-full ${fitCols || fitRows ? 'px-1 py-0.5 text-xs' : 'px-2 py-1 text-sm'}`}>
                    {data[r]?.[c] || ''}
                  </div>
                )}
                {showResizeHandles && c < cols - 1 && (
                  <div className="absolute top-0 right-[-8px] h-full w-6 cursor-col-resize select-none z-40" onMouseDown={(e)=> startDragCol(e, c)}>
                    <div className="absolute top-0 left-1/2 -translate-x-1/2 h-full w-[2px] rounded-full bg-gradient-to-b from-purple-400 via-fuchsia-400 to-pink-400" />
                  </div>
                )}
                {showResizeHandles && r < rows - 1 && (
                  <div className="absolute left-0 bottom-[-8px] w-full h-6 cursor-row-resize select-none z-40" onMouseDown={(e)=> startDragRow(e, r)}>
                    <div className="absolute left-0 top-1/2 -translate-y-1/2 h-[2px] w-full rounded-full bg-gradient-to-r from-purple-400 via-fuchsia-400 to-pink-400" />
                  </div>
                )}
              </div>
            ))}
          </React.Fragment>
        ))}
      </div>
    </div>
  );
};

export default Table;
